import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { createAbsoluteUrl } from "utils/constants";
import { useGlobalSlice } from "app/slice";
import { selectUser } from "app/slice/selectors";
import { useManageProfileSlice } from "app/pages/Customer/ManageProfile/slice";
import { ActivateDialog } from "./activate-dialog";

import { Button } from "app/components/ui/button";
import { Sheet, SheetContent, SheetClose } from "app/components/ui/sheet";
import { Avatar, AvatarImage, AvatarFallback } from "app/components/ui/avatar";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "app/components/ui/accordion";

const getNavItems = (userExists: boolean) => {
  const plansItems = userExists 
    ? [
        { to: "/network-coverage#triple-network-technology", label: "Multi Network" },
        { to: "/family-plans", label: "Family Plans" },
        { to: "/home-phone", label: "Home Phone" },
      ]
    : [
        { to: "/network-plan", label: "Single Network" },
        { to: "/network-coverage#triple-network-technology", label: "Multi Network" },
        { to: "/family-plans", label: "Family Plans" },
        { to: "/home-phone", label: "Home Phone" },
      ];

  const baseItems = [
    {
      type: "dropdown" as const,
      label: "Plans",
      value: "plans",
      items: [
        { to: "/network-coverage#coverage-map", label: "Single Network" },
        { to: "/network-coverage#triple-network-technology", label: "Multi Network" },
        { to: "/family-plans", label: "Family Plans" },
        // { to: "/home-phone", label: "Home Phone" },
      ],
    },
    {
      type: "dropdown" as const,
      label: "Security",
      value: "security",
      items: [
        { to: "/digital-security", label: "Online Security Plans" },
        { to: "/sim-swap-protection", label: "SIM Swap Protection" },
        { to: "/sim-swap-protection#security-protocols", label: "Multi-layer Fraud Prevention" },
      ],
    },
    {
      type: "dropdown" as const,
      label: "Coverage",
      value: "coverage",
      items: [
        { to: "/network-coverage", label: "Domestic Coverage" },
        { to: "/international-calling", label: "International Calling" },
        { to: "/international-calling#international-roaming", label: "International Roaming" },
      ],
    },
    {
      type: "dropdown" as const,
      label: "Support",
      value: "support",
      items: [
        { to: "/imei-check", label: "IMEI Check" },
        { to: "https://vlemobile.tawk.help", label: "Knowledge Base", target: "_blank" },
        { to: "/about-esim", label: "About eSIM" },
        { to: "/how-vle-mobile-works", label: "How VLE Mobile Works" },
        // { to: "/faq", label: "FAQ" },
        { to: createAbsoluteUrl("/faq"), label: "FAQ", target: "_self" },
        // { to: "/resource-center", label: "Blog" },
        { to: createAbsoluteUrl("/resource-center"), label: "Blog", target: "_self" },
        // { to: "/support-center", label: "Contact Us" },
        { to: createAbsoluteUrl("/support-center"), label: "Contact Us", target: "_self" },
        // { to: "/support-tickets", label: "Support Tickets" },
        { to: createAbsoluteUrl("/support-tickets"), label: "Support Tickets", target: "_self" },
        { to: "/about-us", label: "About Us" },
      ],
    },
    { type: "dialog" as const, label: "Activate" },
    { type: "link" as const, to: "/platform-drivers", label: "Platform Drivers" },
    { type: "link" as const, to: "/plans-and-pricing", label: "Pricing" },
    { type: "link" as const, to: "/how-to-switch", label: "How to Switch" },
    { type: "link" as const, to: "/why-choose-vle-mobile", label: "Why VLE Mobile" },
  ];

  // Only show Quick Recharge for non-logged-in users
  // if (!userExists) {
  //   baseItems.splice(baseItems.findIndex(item => item.type === "dialog" && item.label === "Activate") + 1, 0, 
  //     { type: "link" as const, to: "/quick-recharge", label: "Quick Recharge" }
  //   );
  // }

  return baseItems;
};

export const HamburgerMenu = ({ shouldApplyTop }: { shouldApplyTop: boolean }) => {
  const user = useSelector(selectUser);
  const location = useLocation();
  const { pathname } = location;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { actions } = useGlobalSlice();
  const [isOpen, setIsOpen] = useState(false);
  const [showMyAccountMenu, setShowMyAccountMenu] = useState(false);

  const { useLazyGetProfileQuery } = useManageProfileSlice();
  const [
    getProfile,
    {
      data: profileData,
      isLoading: isProfileLoading,
      isError: isProfileError,
    },
  ] = useLazyGetProfileQuery();

  useEffect(() => {
    if (user?._id) {
      getProfile({ customerId: user._id });
    }
  }, [user?._id, getProfile]);

  const handleNavLinkClick = () => {
    setIsOpen(false);
    setShowMyAccountMenu(false);
  };

  const handleProfileClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    if (
      (e as React.KeyboardEvent).type === "keydown" &&
      !["Enter", " ", "Spacebar"].includes((e as React.KeyboardEvent).key)
    ) {
      return;
    }
    setShowMyAccountMenu((prev) => !prev);
  };

  const isAnyChildActive = (items: { to: string; label: string }[]) => {
    const currentPath = pathname + (location.hash || '');
    return items.some(item => currentPath === item.to);
  };

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) setShowMyAccountMenu(false);
      }}
    >
      <Button
        type="button"
        variant="ghost"
        className="p-0.5 rounded-md ml-1 hover:bg-transparent hover:brightness-200 lg:hidden focus:ring-1 focus:ring-black"
        aria-label="Menu"
        onClick={(e) => {
          e.stopPropagation();
          if (isOpen && showMyAccountMenu) {
            setShowMyAccountMenu(false);
          } else if (!isOpen) {
            setIsOpen(true);
            setShowMyAccountMenu(false);
          }
        }}
      >
        <img
          src="/assets/hamburger.svg"
          alt="Menu"
          className="shrink-0 size-6 sm:size-7 pt-0.5"
        />
      </Button>
      <SheetContent 
        side="right" 
        sheetOverlayClassName={
          showMyAccountMenu
            ? shouldApplyTop
              ? "top-24 sm:top-[7.5rem]"
              : "top-16 sm:top-20"
            : ""
        }
        className={`p-0 ${
          showMyAccountMenu 
            ? `${shouldApplyTop ? 'top-24 sm:top-[7.5rem]' : 'top-16 sm:top-20'} w-screen max-w-full sm:max-w-full`
            : 'w-[18.1875rem]'
        }`}
      >
        <div
          className={`flex flex-col bg-white p-3.5 pt-1 shadow-lg ${
            showMyAccountMenu 
              ? `${shouldApplyTop ? 'h-[calc(100dvh-6rem)] sm:h-[calc(100vh-7.5rem)]' : 'h-[calc(100dvh-4rem)] sm:h-[calc(100vh-5rem)]'} pt-0`
              : 'h-full'
          }`}
        >
          {/* {!showMyAccountMenu && (
            user ? (
              <div
                className="flex items-center justify-between pb-4 border-b border-b-[#9C939333] cursor-pointer focus:outline-none"
                tabIndex={0}
                role="button"
                onClick={handleProfileClick}
                onKeyDown={handleProfileClick}
                aria-label={showMyAccountMenu ? "Back to main menu" : "Open My Account menu"}
              >
                <div className="flex gap-2 items-center">
                  <Avatar className="items-center justify-center bg-[#BFBFBF1F] size-9 rounded-full">
                    <AvatarImage 
                      src={profileData?.data?.image || "/assets/profile.svg"}  
                      alt="Profile" 
                      className={profileData?.data?.image ? "size-full rounded-full" : "size-5"}
                    />
                    <AvatarFallback className="bg-[#BFBFBF1F] uppercase text-black text-sm font-medium leading-3">
                      {user?.firstName?.charAt(0) || profileData?.data?.firstName?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="inline-block capitalize text-[#181818] text-sm font-semibold leading-[0.875rem]">
                    {user?.firstName} {user?.lastName}
                  </span>
                </div>
                <img
                  src="/assets/chevron-down.svg"
                  alt={showMyAccountMenu ? "Back" : "Chevron Down"}
                  className={showMyAccountMenu ? "rotate-90 size-5 shrink-0" : "-rotate-90 size-5 shrink-0"}
                  aria-hidden="true"
                />
              </div>
            ) : (
              <div className="flex gap-2 items-center pb-4 border-b border-b-[#9C939333]">
                <SheetClose asChild>
                  <Link to="/login" className="rounded-[6.25rem]">
                    <Button type="button" className="bg-transparent hover:bg-[#FAFAFA] min-w-[4.65rem] sm:min-w-20 h-10 sm:h-11 px-3 py-2 sm:p-[0.875rem_1.125rem] border border-[#F2EEE9] rounded-[6.25rem] text-[#1D1D1B] text-xs sm:text-sm font-semibold leading-[1.575rem] hover:shadow-sm">
                      Log in
                    </Button>
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link to="/network-plan" className="rounded-[6.25rem]">
                    <Button type="button" className="bg-[#1D1D1B] hover:bg-black min-w-[7.5rem] sm:min-w-32 h-10 sm:h-11 px-3 py-2 sm:p-[0.875rem_1.125rem] border border-[#F2EEE9] rounded-[6.25rem] text-white text-xs sm:text-sm font-semibold leading-[1.575rem] hover:shadow-md">
                      Start for $1 -&gt;
                    </Button>
                  </Link>
                </SheetClose>
              </div>
            )
          )} */}
          {/* {user && showMyAccountMenu ? (
            <div className="flex overflow-y-auto flex-col flex-1 mt-2">
              {myAccountItems.map((item, index) => (
                <SheetClose asChild key={item.label}>
                  <div className={`py-2.5 ${myAccountItems.length - 1 === index ? "" : "border-b border-b-[#9C939333]"}`}>
                    {item.label === "Sign Out" ? (
                      <div
                        className="flex w-full text-sm leading-[1.8125rem] text-[#FE4D01] hover:text-[#E44501] font-semibold cursor-pointer"
                        onClick={() => {
                          dispatch(actions.setUser({ 
                            user: null,
                            rememberDevice: false
                          }));
                          navigate("/");
                          setIsOpen(false);
                        }}
                      >
                        {item.label}
                      </div>
                    ) : (
                      <NavLink
                        to={item.to || ""}
                        onClick={handleNavLinkClick}
                        className={({ isActive }) =>
                          `flex w-full text-sm leading-[1.8125rem] hover:text-[#FE4D01] ${
                            isActive ? "text-[#1D1D1B] font-semibold" : "text-[#464646] font-medium"
                          }`
                        }
                        tabIndex={0}
                        aria-label={item.label}
                      >
                        <div className="inline-flex justify-between items-center w-full">
                          {item.label}
                          <img src="/assets/chevron-down.svg" alt="Chevron Down" className="-rotate-90 size-5 shrink-0" aria-hidden="true" />
                        </div>
                      </NavLink>
                    )}
                  </div>
                </SheetClose>
              ))}
            </div>
          ) : ( */}
            <Accordion type="single" className="overflow-y-auto overflow-x-hidden flex-1" collapsible>
              {getNavItems(!!user).map((item, index) => (
                <div key={item.label}>
                  {item.type === "dropdown" ? (
                    <AccordionItem value={item.value || ""} className="py-2.5 border-b border-b-[#9C939333]">
                      <AccordionTrigger className="group py-0 hover:no-underline [&>img:last-child]:-rotate-90 [&[data-state=open]>img:last-child]:rotate-0 [&>img:last-child]:transition-transform [&>img:last-child]:duration-200 [&>img:last-child]:ease-in-out">
                        <span className={`inline-block text-sm font-medium leading-[1.8125rem] group-hover:text-[#FE4D01] ${
                            isAnyChildActive(item.items || []) ? "text-[#1D1D1B] font-semibold" : "text-[#464646] font-medium"
                          }`}
                        >
                          {item.label}
                        </span>
                        <img src="/assets/chevron-down.svg" alt="Chevron Down" className="size-5 shrink-0" aria-hidden="true" />
                      </AccordionTrigger>
                      <AccordionContent className="pb-1">
                        {item.items?.map((subItem) => (
                          subItem.target ? (
                            <a
                              key={subItem.label}
                              href={subItem.to}
                              target={subItem.target || '_blank'}
                              rel="noopener noreferrer"
                              onClick={handleNavLinkClick}
                              className="block w-full pl-5 pt-2.5 last:pb-0 border-l border-l-[#9C939366] ml-2 text-[0.825rem] leading-normal text-[#464646] font-normal hover:text-black"
                            >
                              {subItem.label}
                            </a>
                          ) : (
                            <NavLink
                              key={subItem.label}
                              to={subItem.to}
                              onClick={handleNavLinkClick}
                            >
                              {() => {
                                const currentPath = pathname + (location.hash || '');
                                const isExactMatch = currentPath === subItem.to;
                                
                                return (
                                  <div className={`relative w-full pl-5 pt-2.5 last:pb-0 border-l border-l-[#9C939366] ml-2 text-[0.825rem] leading-normal ${
                                      isExactMatch ? "text-[#FE4D01] font-medium" : "text-[#464646] font-normal hover:text-black"
                                    }`}
                                  >
                                    {subItem.label}
                                    {isExactMatch && <img src="/assets/tick-orange.svg" alt="Tick" className="shrink-0 size-4 absolute top-[0.8125rem] right-2.5" aria-hidden="true" />}
                                  </div>
                                );
                              }}
                            </NavLink>
                          )
                        ))}
                      </AccordionContent>
                    </AccordionItem>
                  ) : (
                    <div className={`py-2.5 ${getNavItems(!!user).length - 1 === index ? "" : "border-b border-b-[#9C939333]"}`}>
                      {item.type === "dialog" ? (
                        <ActivateDialog />
                      ) : (
                        <NavLink
                          to={item.to || ""}
                          onClick={handleNavLinkClick}
                          className={({ isActive }) =>
                            `flex w-full text-sm leading-[1.8125rem] hover:text-[#FE4D01] ${
                              isActive ? "text-[#1D1D1B] font-semibold" : "text-[#464646] font-medium"
                            }`
                          }
                        >
                          <div className="inline-flex justify-between items-center w-full">
                            {item.label}
                            <img src="/assets/chevron-down.svg" alt="Chevron Down" className="-rotate-90 size-5 shrink-0" aria-hidden="true" />
                          </div>
                        </NavLink>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </Accordion>
          {/* )} */}
          <div className="flex justify-center items-center mt-4">
            <div className="shrink-0 w-[3.50156rem] h-[2.18769rem]">
              <img
                src="/assets/vle-logo.svg"
                className="size-full"
                alt="vle mobile"
              />
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
